import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import axios from 'axios';

import { PeriodicType } from 'app/shared/model/enumerations/periodicType';
import { IIdentificationCodeAlert, defaultValue } from 'app/shared/model/identification-code-alert.model';
import { EntityState, IQueryParams, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';

const initialState: EntityState<IIdentificationCodeAlert> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/identification-code-alerts';

// Actions

export const getEntities = createAsyncThunk('identificationCodeAlert/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IIdentificationCodeAlert[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'identificationCodeAlert/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IIdentificationCodeAlert>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'identificationCodeAlert/create_entity',
  async (entity: IIdentificationCodeAlert, thunkAPI) => {
    const result = await axios.post<IIdentificationCodeAlert>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'identificationCodeAlert/update_entity',
  async (entity: IIdentificationCodeAlert, thunkAPI) => {
    const result = await axios.put<IIdentificationCodeAlert>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'identificationCodeAlert/partial_update_entity',
  async (entity: IIdentificationCodeAlert, thunkAPI) => {
    const result = await axios.patch<IIdentificationCodeAlert>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'identificationCodeAlert/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IIdentificationCodeAlert>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

interface IQueryFilterParams extends IQueryParams {
  searchCompanyId?: number | null;
  dateStartPeriodic?: string | number | null;
  dateEndPeriodic?: string | number | null;
  periodicType?: PeriodicType | null;
  nameSearch?: string | null;
  licensePlateSearch?: string | null;
  truckImplementId?: string | number;
}

export const getIdentificationCodeAlertsActivationByCompanyAndDateStartAndDateEnd = createAsyncThunk(
  'identificationCodeAlert/fetch_entity_list',
  async ({ page, size, sort, dateStartPeriodic, dateEndPeriodic, nameSearch, licensePlateSearch, searchCompanyId, truckImplementId }: IQueryFilterParams) => {
    const requestUrl = `${apiUrl}${`/company/${dateStartPeriodic}/${dateEndPeriodic}?page=${page}&size=${size}&sort=${sort}&valueSearch=${nameSearch}&licensePlateSearch=${licensePlateSearch}&cacheBuster=${new Date().getTime()}`}`;
    return axios.get<IIdentificationCodeAlert[]>(requestUrl, { params: { searchCompanyId, truckImplementId } });
  }
);

export const getIdentificationCodeAlertsActivationByCompanyAndPeriodic = createAsyncThunk(
  'identificationCodeAlert/fetch_entity_list',
  async ({ page, size, sort, periodicType, nameSearch, licensePlateSearch, searchCompanyId, truckImplementId }: IQueryFilterParams) => {
    const requestUrl = `${apiUrl}${`/company/${periodicType}?page=${page}&size=${size}&sort=${sort}&valueSearch=${nameSearch}&licensePlateSearch=${licensePlateSearch}&cacheBuster=${new Date().getTime()}`}`;
    return axios.get<IIdentificationCodeAlert[]>(requestUrl, { params: { searchCompanyId, truckImplementId } });
  }
);

export const getIdentificationCodeAlertsActivationByTruckImplementAndDateStartAndDateEnd = createAsyncThunk(
  'identificationCodeAlert/fetch_infractions_by_truck_implement_and_date_start_and_date_end',
  async ({ page, size, sort, dateStartPeriodic, dateEndPeriodic, nameSearch, truckImplementId }: IQueryFilterParams) => {
    const requestUrl = `${apiUrl}${`/truck-implement/${truckImplementId}/${dateStartPeriodic}/${dateEndPeriodic}?page=${page}&size=${size}&sort=${sort}&valueSearch=${nameSearch}&cacheBuster=${new Date().getTime()}`}`;
    return axios.get<IIdentificationCodeAlert[]>(requestUrl);
  }
);

export const getIdentificationCodeAlertsActivationByTruckImplementAndPeriodic = createAsyncThunk(
  'identificationCodeAlert/fetch_infractions_by_truck_implement_and_periodic',
  async ({ page, size, sort, periodicType, nameSearch, truckImplementId }: IQueryFilterParams) => {
    const requestUrl = `${apiUrl}${`/truck-implement/${truckImplementId}/${periodicType}?page=${page}&size=${size}&sort=${sort}&valueSearch=${nameSearch}&cacheBuster=${new Date().getTime()}`}`;
    return axios.get<IIdentificationCodeAlert[]>(requestUrl);
  }
);

// slice

export const IdentificationCodeAlertSlice = createEntitySlice({
  name: 'identificationCodeAlert',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getIdentificationCodeAlertsActivationByTruckImplementAndDateStartAndDateEnd), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getIdentificationCodeAlertsActivationByTruckImplementAndPeriodic), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getIdentificationCodeAlertsActivationByCompanyAndDateStartAndDateEnd), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getIdentificationCodeAlertsActivationByCompanyAndPeriodic), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(
        isPending(
          getEntities,
          getEntity,
          getIdentificationCodeAlertsActivationByTruckImplementAndDateStartAndDateEnd,
          getIdentificationCodeAlertsActivationByTruckImplementAndPeriodic,
          getIdentificationCodeAlertsActivationByCompanyAndDateStartAndDateEnd,
          getIdentificationCodeAlertsActivationByCompanyAndPeriodic
        ),
        state => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.loading = true;
        }
      )
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = IdentificationCodeAlertSlice.actions;

// Reducer
export default IdentificationCodeAlertSlice.reducer;
